import React, { useState } from 'react';
import { useStreaks } from '../hooks/useStreaks';

const StatsBanner = ({ logs, compact = false }) => {
    const { currentStreak, longestStreak, weeklyCount, weeklyDayStatuses } = useStreaks(logs || []);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const getFlameStyles = (count) => {
        if (count >= 4) return "text-teal-400 drop-shadow-[0_0_15px_rgba(45,212,191,0.8)]";
        if (count >= 2) return "text-orange-500 drop-shadow-[0_0_12px_rgba(249,115,22,0.7)] animate-pulse";
        return "text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.6)]";
    };

    if (compact) {
        return (
            <div className="bg-gray-800/40 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-gray-700/50 text-gray-100 mb-8 flex justify-around items-center">
                <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-6 h-6 transition-all duration-300 ${getFlameStyles(weeklyCount)}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                    </svg>
                    <div>
                        <p className="text-xl font-bold text-white leading-none">{currentStreak}</p>
                        <p className="text-xs uppercase text-gray-400 mt-0.5 font-medium tracking-wide">Week Streak</p>
                    </div>
                </div>
                <div className="h-8 w-px bg-gray-700/50"></div>
                <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <div>
                         <p className="text-xl font-bold text-white leading-none">{longestStreak}</p>
                         <p className="text-xs uppercase text-gray-400 mt-0.5 font-medium tracking-wide">Best Streak</p>
                    </div>
                </div>
                <div className="h-8 w-px bg-gray-700/50"></div>
                <div className="flex flex-col items-center">
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm font-bold text-white leading-none">{weeklyCount}<span className="text-gray-400 ml-1">/ 7</span></p>
                    </div>
                    <div className="flex gap-1">
                        {weeklyDayStatuses && weeklyDayStatuses.map((isCompleted, i) => (
                            <span 
                                key={i} 
                                className={`w-1.5 h-1.5 rounded-full ${isCompleted ? 'bg-teal-400 shadow-[0_0_5px_rgba(45,212,191,0.6)]' : 'bg-gray-600'}`}
                            ></span>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-gray-800/40 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-700/50 text-gray-100 mb-8">
                <div className="flex justify-center items-center gap-2 mb-6">
                    <h2 className="text-xl font-bold text-teal-300 drop-shadow-sm">My Stats</h2>
                    <button 
                        onClick={() => setIsModalOpen(true)} 
                        className="text-gray-400 hover:text-white transition-colors focus:outline-none" 
                        aria-label="Info about streaks"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>
                </div>
                
                <div className="flex flex-row justify-between items-center text-center">
                    {/* Current Streak */}
                    <div className="flex-1 border-r border-gray-700/50 px-2">
                        <div className="flex justify-center mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className={`w-10 h-10 transition-all duration-300 ${getFlameStyles(weeklyCount)}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                            </svg>
                        </div>
                        <p className="text-3xl font-bold text-white">{currentStreak}</p>
                        <p className="text-xs uppercase tracking-widest text-gray-400 mt-2 font-medium">WEEKS IN A ROW</p>
                    </div>

                    {/* Longest Streak */}
                    <div className="flex-1 border-r border-gray-700/50 px-2">
                        <div className="flex justify-center mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                        </div>
                        <p className="text-3xl font-bold text-white">{longestStreak}</p>
                        <p className="text-xs uppercase tracking-widest text-gray-400 mt-2 font-medium">BEST (WEEKS)</p>
                    </div>

                    {/* This Week */}
                    <div className="flex-1 px-2">
                        <div className="flex justify-center mb-3">
                             <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-teal-400 drop-shadow-[0_0_8px_rgba(45,212,191,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p className="text-3xl font-bold text-white mb-2">{weeklyCount}<span className="text-xl text-gray-400 font-medium ml-1">/ 7</span></p>
                        <div className="flex justify-center gap-1.5 mt-2">
                            {weeklyDayStatuses && weeklyDayStatuses.map((isCompleted, i) => (
                                <span 
                                    key={i} 
                                    className={`w-2.5 h-2.5 rounded-full ${isCompleted ? 'bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.8)]' : 'bg-gray-700'}`}
                                ></span>
                            ))}
                        </div>
                        <p className="text-xs uppercase tracking-widest text-gray-400 mt-2 font-medium">This Week</p>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-sm w-full shadow-2xl mx-4 relative overflow-hidden">
                        <h3 className="text-teal-300 font-bold text-xl mb-3">🔥 How Streaks Work</h3>
                        <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                            Log at least one session a week to keep your streak alive. The more days you train in a week, the hotter your streak flame burns! Miss a full week, and your streak resets to zero.
                        </p>
                        <button 
                            onClick={() => setIsModalOpen(false)}
                            className="bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-4 py-2 w-full font-semibold transition-colors"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default React.memo(StatsBanner);
